import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { RootAttributes } from "@/stores/EngineStore/interfaces/RootAttributes.ts";
import { useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

export const useRootAttributes = (divElement: HTMLDivElement) => {
  const { setRootAttributes, page } = useEngineStore(
    useShallow((state) => ({
      setRootAttributes: state.setRootAttributes,
      page: state.page,
    })),
  );

  const divAttributes = useMemo(() => {
    const attrsObj: Record<RootAttribute, string | string[] | boolean | undefined> = {
      [RootAttribute.ApiUrl]: undefined,
      [RootAttribute.HideLanguagePicker]: undefined,
      [RootAttribute.HideParcelVehiclePicker]: undefined,
      [RootAttribute.ParcelVehiclePickerDimensionsMandatory]: undefined,
      [RootAttribute.Languages]: [],
      [RootAttribute.ExcludeDates]: false,
      [RootAttribute.GTM_ID]: undefined,
      [RootAttribute.HideTitle]: false,
      [RootAttribute.ShowAdultAgePicker]: false,
      [RootAttribute.HideCategoryPicker]: false,
      [RootAttribute.DisableRateGroupContinue]: [],
      [RootAttribute.UseClassicRatePlanDetails]: false,
      [RootAttribute.AccommodationTypesForPayment]: [],
      [RootAttribute.RateGroupsForPayment]: [],
    };

    Array.from(divElement.attributes).forEach((attr) => {
      const newKey: RootAttribute | string = attr.name.includes("data")
        ? attr.name.split("data-")[1].replace(/-/g, "_")
        : attr.name;

      if (!isRootAttribute(newKey)) return;

      let attrValue: string = attr.value;
      if (isJson(attr.value)) attrValue = JSON.parse(attr.value);

      // Parse array values
      if (Array.isArray(attrValue)) {
        attrsObj[newKey as RootAttribute] = attrValue;
        return;
      }
      // Parse boolean values
      if (attrValue.toLowerCase() === "true" || attrValue.toLowerCase() === "false") {
        attrsObj[newKey as RootAttribute] = attrValue.toLowerCase() === "true";
        return;
      }
      // Parse string values
      attrsObj[newKey as RootAttribute] = attrValue;
    });

    return attrsObj;
  }, [divElement.attributes]);

  useEffect(() => {
    setRootAttributes(divAttributes as unknown as RootAttributes);
  }, [setRootAttributes, divAttributes, page]);
};

export const isJson = (str?: string) => {
  if (typeof str !== "string") return false;
  try {
    const result = JSON.parse(str);
    const type = Object.prototype.toString.call(result);
    return type === "[object Object]" || type === "[object Array]";
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return false;
  }
};

export const isRootAttribute = (key: string | RootAttribute): boolean => {
  return Object.values(RootAttribute).includes(key as RootAttribute);
};
